import * as S from "effect-app/Schema"
import { ConditionAction, type ConditionActionType } from "./Condition.ts"
import { ConfiguratorBase, ConfiguratorOptionGroup, ConfiguratorPriceTable } from "./Configurator.ts"
import { ListOptionCategory, ListOptionItem, NumberOption, OptionBase, TextInfoOption } from "./Option.ts"

export const LabelTargetKind = S.Literals([
  "configuratorTitle",
  "optionGroup",
  "priceTable",
  "option",
  "listItem",
  "listCategory",
  "conditionAction"
])
export type LabelTargetKind = typeof LabelTargetKind.Type

export const LabelField = S.Literals([
  "title",
  "name",
  "subTitle",
  "description",
  "tooltip",
  "inputPlaceholder",
  "prefix",
  "suffix",
  "stepPriceDescription",
  "typeValue"
])
export type LabelField = typeof LabelField.Type

// The condition actions whose typeValue is a text the end user reads. Every
// other action carries an id, a number, a formula, a URL or a flag. Set
// value / default value are only text when the target option is a text
// option (an item id on lists, a number on number options), see
// isTextConditionAction.
const textConditionActionTypes: ReadonlySet<ConditionActionType> = new Set<ConditionActionType>([
  "OPTION_ADD_CUSTOMER_NOTE",
  "OPTION_SET_TOOLTIP",
  "NUMBER_OPTION_SET_STEP_PRICE_DESCRIPTION"
])
const textOptionValueActionTypes: ReadonlySet<ConditionActionType> = new Set<ConditionActionType>([
  "OPTION_SET_VALUE",
  "OPTION_SET_DEFAULT_VALUE"
])

export function isTextConditionAction(type: ConditionActionType, targetOptionTag: string | undefined): boolean {
  return textConditionActionTypes.has(type) || (textOptionValueActionTypes.has(type) && targetOptionTag === "text")
}

// Wire-level cap for any label text: the widest field is the option
// description (NonEmptyString2k). The tighter per-field 255 limits come from
// labelMaxLength below, enforced in the form, the controller and the core.
export const MAX_LABEL_TEXT = 2000

// The per-(kind, field) source of truth is the aggregate's own field schemas:
// requiredness and length caps are read off them rather than re-stated by
// hand, so a model change cannot silently diverge from the label rules.
const labelFieldSchemas: Record<LabelTargetKind, Partial<Record<LabelField, S.Top>>> = {
  configuratorTitle: { title: ConfiguratorBase.fields.title },
  optionGroup: { name: ConfiguratorOptionGroup.fields.name },
  priceTable: { name: ConfiguratorPriceTable.fields.name },
  option: {
    name: OptionBase.name,
    description: OptionBase.description,
    tooltip: OptionBase.tooltip,
    inputPlaceholder: NumberOption.fields.inputPlaceholder,
    prefix: NumberOption.fields.prefix,
    suffix: NumberOption.fields.suffix,
    stepPriceDescription: NumberOption.fields.stepPriceDescription,
    // text_info's extra title, the one optional title in the model
    title: TextInfoOption.fields.title
  },
  listItem: {
    title: ListOptionItem.fields.title,
    subTitle: ListOptionItem.fields.subTitle,
    description: ListOptionItem.fields.description,
    tooltip: ListOptionItem.fields.tooltip
  },
  listCategory: {
    title: ListOptionCategory.fields.title,
    tooltip: ListOptionCategory.fields.tooltip
  },
  // Nullable like the model: an empty text has meaning for the frontend
  // (e.g. OPTION_SET_TOOLTIP with null removes the tooltip), and an action
  // saved without its text must not block the bulk save as a required row.
  conditionAction: { typeValue: ConditionAction.fields.typeValue }
}

// Precomputed once: probing per keystroke would rebuild guards for nothing.
// A field is required iff its schema rejects null; it is "wide" iff its
// schema accepts a 256-char text (the model only has the 255/2k widths).
const labelFieldMeta = (() => {
  const required = new Set<string>()
  const wide = new Set<string>()
  const overCap255 = "x".repeat(256)
  for (const [kind, fields] of Object.entries(labelFieldSchemas)) {
    for (const [field, schema] of Object.entries(fields)) {
      const is = S.is(schema)
      if (!is(null)) required.add(`${kind}|${field}`)
      if (is(overCap255)) wide.add(`${kind}|${field}`)
    }
  }
  return { required, wide }
})()

// Required fields must stay non-empty; everything else clears to null on "".
export function isRequiredLabelField(kind: LabelTargetKind, field: LabelField): boolean {
  return labelFieldMeta.required.has(`${kind}|${field}`)
}

// Per-field cap, read off the field schemas (see labelFieldMeta).
export function labelMaxLength(kind: LabelTargetKind, field: LabelField): number {
  return labelFieldMeta.wide.has(`${kind}|${field}`) ? MAX_LABEL_TEXT : 255
}

export class LabelTarget extends S.Opaque<LabelTarget, LabelTarget.Encoded>()(
  S
    .Struct({
      kind: LabelTargetKind,
      // the entity's own id; null only for the configurator title, which is a
      // field on the configurator rather than an object of its own.
      id: S.NullOr(S.StringId),
      field: LabelField,
      // human-readable context path ("Gruppe > Option: Element"), read-only
      label: S.NonEmptyString2k,
      // the current value; "" when the field is unset
      text: S.String.pipe(S.check(S.isMaxLength(MAX_LABEL_TEXT)))
    })
    .annotate({ identifier: "LabelTarget" })
) {}

export class LabelTargetAddress extends S.Opaque<LabelTargetAddress, LabelTargetAddress.Encoded>()(
  S
    .Struct({
      kind: LabelTargetKind,
      id: S.NullOr(S.StringId),
      field: LabelField
    })
    .annotate({ identifier: "LabelTargetAddress" })
) {}

export class LabelUpdateChange extends S.Opaque<LabelUpdateChange, LabelUpdateChange.Encoded>()(
  S
    .Struct({
      kind: LabelTargetKind,
      id: S.NullOr(S.StringId),
      field: LabelField,
      // declarative final value. Trimmed server-side; "" clears an optional
      // field and is rejected for a required one (see the controller).
      text: S.String.pipe(S.check(S.isMaxLength(MAX_LABEL_TEXT)))
    })
    .annotate({ identifier: "LabelUpdateChange" })
) {}

// codegen:start {preset: model}
//
export namespace LabelTarget {
  export interface Encoded extends S.StructNestedEncoded<typeof LabelTarget> {}
}
export namespace LabelTargetAddress {
  export interface Encoded extends S.StructNestedEncoded<typeof LabelTargetAddress> {}
}
export namespace LabelUpdateChange {
  export interface Encoded extends S.StructNestedEncoded<typeof LabelUpdateChange> {}
}
//
// codegen:end
